import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../ui/Icon';
import { Modal } from '../../ui/Modal';

export const MAX_PHOTOS = 4;
export interface PhotoAnnotation { caption: string; location: string; checklistItem: string }

/** Rear-camera capture plus gallery upload, up to four photos, with previews. */
export function PhotoEvidence({ id, title, files, onChange, annotations, onAnnotationsChange }: { id: string; title: string; files: File[]; onChange(files: File[]): void; annotations?: PhotoAnnotation[]; onAnnotationsChange?(annotations: PhotoAnnotation[]): void }) {
  const previews = useMemo(() => files.map((file) => ({ file, url: URL.createObjectURL(file) })), [files]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState('');
  useEffect(() => () => previews.forEach((preview) => URL.revokeObjectURL(preview.url)), [previews]);
  useEffect(() => {
    if (!stream || !videoRef.current) return;
    videoRef.current.srcObject = stream;
    videoRef.current.play().catch(() => undefined);
    return () => stream.getTracks().forEach((track) => track.stop());
  }, [stream]);

  function add(next: File[]) {
    onChange([...files, ...next].slice(0, MAX_PHOTOS));
    if (onAnnotationsChange) onAnnotationsChange([...annotations || [], ...next.map(() => ({ caption: '', location: '', checklistItem: '' }))].slice(0, MAX_PHOTOS));
  }

  function closeCamera() {
    stream?.getTracks().forEach((track) => track.stop());
    setStream(null);
  }

  async function openCamera() {
    setCameraError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('This browser cannot access a camera. Use Add photos instead.');
      return;
    }
    try {
      const next = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
      setStream(next);
    } catch {
      setCameraError('Camera access was not granted. Check the browser permission and try again.');
    }
  }

  function capturePhoto() {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) {
      setCameraError('The camera is still starting. Try again in a moment.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return setCameraError('The photo could not be captured. Please try again.');
      add([new File([blob], `site-photo-${Date.now()}.jpg`, { type: 'image/jpeg' })]);
      closeCamera();
    }, 'image/jpeg', 0.9);
  }

  const full = files.length >= MAX_PHOTOS;
  return (
    <section className="photo-evidence">
      <div className="photo-evidence-heading"><b>{title}</b><small>Up to four site photos</small></div>
      <div className="photo-evidence-actions">
        {!stream && <button type="button" className="btn btn-secondary btn-sm" onClick={openCamera} disabled={full}>Camera</button>}
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => photoInputRef.current?.click()} disabled={full}>Add photos</button>
        <input ref={photoInputRef} id={`${id}Photos`} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={full} onChange={(event) => { add(Array.from(event.target.files || [])); event.target.value = ''; }} />
      </div>
      {stream && (
        <Modal id={`${id}CameraCapture`} className="camera-capture-modal" backdropClassName="camera-capture-backdrop" labelledBy={`${id}CameraTitle`} dismissable={false}>
          <div className="photo-camera">
            <video id={`${id}CameraPreview`} ref={videoRef} autoPlay muted playsInline aria-label="Live camera preview" />
            <div className="camera-capture-controls">
              <p id={`${id}CameraTitle`}>Camera</p>
              <div className="photo-evidence-actions"><button type="button" className="btn btn-primary" onClick={capturePhoto}>Use photo</button><button type="button" className="btn btn-secondary" onClick={closeCamera}>Cancel</button></div>
            </div>
          </div>
        </Modal>
      )}
      {cameraError && <p className="form-error" role="alert">{cameraError}</p>}
      <p className="form-help photo-status" aria-live="polite">{files.length ? `${files.length} photo${files.length === 1 ? '' : 's'} ready to save.${full ? ' Maximum reached.' : ''}` : ''}</p>
      <div className="photo-grid" aria-label={`Selected ${title}`}>
        {previews.map((preview, index) => (
          <figure key={preview.url}>
            <img src={preview.url} alt={preview.file.name} />
            <button type="button" className="photo-remove" aria-label={`Remove ${preview.file.name}`} onClick={() => { onChange(files.filter((_, item) => item !== index)); onAnnotationsChange?.((annotations || []).filter((_, item) => item !== index)); }}><Icon name="close" size={14} /></button>
            {annotations && <figcaption className="photo-annotation"><label>Caption <input id={`${id}Caption-${index}`} value={annotations[index]?.caption || ''} onChange={(event) => onAnnotationsChange?.(annotations.map((item, at) => at === index ? { ...item, caption: event.target.value } : item))} /></label><label>Location <input id={`${id}Location-${index}`} value={annotations[index]?.location || ''} onChange={(event) => onAnnotationsChange?.(annotations.map((item, at) => at === index ? { ...item, location: event.target.value } : item))} /></label><label>Related finding / checklist item <input id={`${id}ChecklistItem-${index}`} value={annotations[index]?.checklistItem || ''} onChange={(event) => onAnnotationsChange?.(annotations.map((item, at) => at === index ? { ...item, checklistItem: event.target.value } : item))} /></label></figcaption>}
          </figure>
        ))}
      </div>
    </section>
  );
}
